import axios from "axios";
import Configs from "../utils/Configs";
import StorageKeys from "../constants/storageKeys";
import AppConstants from "../constants/appConstants";
import {
  cacheAccessTokenForServiceWorker,
  clearCachedAccessTokenForServiceWorker,
} from "../utils/tokenIndexedDb";

const axiosInstance = axios.create({
  baseURL: Configs.baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

let isRefreshing = false;
let failedRequestsQueue = [];

// --- Helpers ---
const processQueue = (error, token = null) => {
  failedRequestsQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedRequestsQueue = [];
};

const redirectToLogin = (delayMs = 300) => {
  console.warn("[AUTH] Session expired. Logging out...");

  setTimeout(() => {
    // Clear only the session's own data — a blanket localStorage.clear()
    // would also wipe unrelated saved state like cookie consent.
    localStorage.removeItem(StorageKeys.USER);
    localStorage.removeItem(StorageKeys.ACCESS_TOKEN);
    localStorage.removeItem(StorageKeys.REFRESH_TOKEN);
    sessionStorage.clear();
    clearCachedAccessTokenForServiceWorker();

    // Use replace so back button can’t return to dead session
    window.location.replace("/auth/login-page");
  }, delayMs);
};

// --- Request Interceptor ---
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(StorageKeys.ACCESS_TOKEN);
    console.log(token);
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// --- Response Interceptor ---
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const data = error.response?.data;

    // Paid feature called after the single's subscription ran out (backend
    // subscription_required). Send them to the renew page — once, however
    // many requests on the current page failed this way.
    if (status === 402 && data?.data?.message === "subscription_expired") {
      const renewPath = AppConstants.RENEW_SUBSCRIPTION_PATH;
      if (window.location.pathname !== renewPath) {
        window.location.replace(renewPath);
      }
      return Promise.reject(error);
    }

    const isTokenInvalid =
      status === 401 || (status === 403 && data?.code === "token_not_valid");

    if (isTokenInvalid && !originalRequest._isRetry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${token}`,
            };
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._isRetry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(StorageKeys.REFRESH_TOKEN);

      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          Configs.tokenRefreshEp, // full URL
          { refresh: refreshToken },
          { headers: { "Content-Type": "application/json" } },
        );

        const { access, refresh } = response.data;

        // ✅ Only replace tokens if refresh succeeded
        if (access) {
          console.log(access);
          localStorage.setItem(StorageKeys.ACCESS_TOKEN, access);
          cacheAccessTokenForServiceWorker(access);
          if (refresh) {
            localStorage.setItem(StorageKeys.REFRESH_TOKEN, refresh);
          }

          axiosInstance.defaults.headers.Authorization = `Bearer ${access}`;
          processQueue(null, access);

          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${access}`,
          };

          return axiosInstance(originalRequest);
        } else {
          // Refresh endpoint didn’t return a valid token
          redirectToLogin();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
